<script setup lang="ts">
import * as v from 'valibot'
import type { BlogPostProps } from '@nuxt/ui'
import type { Form, FormSubmitEvent } from '#ui/types'

/** Casos de ejemplo para la sección «Tres ejemplos de clientes reales». */
interface HoteliersBlogPost {
  badge: string
  title: string
  image: string
  description: string
  revenue: string
  bookings: string
}

/** Perfiles de demanda en formato blog (imagen + texto). */
interface DemandProfilePost extends BlogPostProps {
  paragraph: string
}

/** Los slots de `UBlogPosts` tipan `post` como `BlogPostProps`; aquí reflejamos el payload real. */
function asHoteliersBlogPost(post: BlogPostProps): HoteliersBlogPost {
  return post as unknown as HoteliersBlogPost
}

/** Entrada del array `hoteliersPage.testimonials` en los JSON de idioma. */
interface HoteliersTestimonialEntry {
  quote: string
  name: string
  role: string
  kind: string
}

const { t, tm, locale } = useI18n()
const localePath = useLocalePath()
const deviceStore = useDeviceStore()
const { trackHoteliersLeadFormSubmitted } = usePosthogFunnel()

const schema = computed(() =>
  v.object({
    hotelName: v.pipe(v.string(), v.trim(), v.minLength(1, t('hoteliersPage.validation.hotelName'))),
    website: v.optional(
      v.pipe(v.string(), v.trim(), v.maxLength(500, t('hoteliersPage.validation.websiteMax')))
    ),
    rooms: v.pipe(
      v.string(),
      v.picklist(['1-9', '10-19', '20-59', '60-99', '100+'], t('hoteliersPage.validation.rooms'))
    ),
    cityCountry: v.pipe(v.string(), v.trim(), v.minLength(1, t('hoteliersPage.validation.cityCountry'))),
    contactName: v.pipe(v.string(), v.trim(), v.minLength(1, t('hoteliersPage.validation.contactName'))),
    email: v.pipe(
      v.string(),
      v.trim(),
      v.minLength(1, t('hoteliersPage.validation.emailRequired')),
      v.email(t('hoteliersPage.validation.emailInvalid'))
    ),
    phone: v.pipe(v.string(), v.trim(), v.minLength(1, t('hoteliersPage.validation.phone'))),
    comments: v.pipe(v.string(), v.maxLength(4000, t('hoteliersPage.validation.commentsMax'))),
    privacyAccepted: v.pipe(
      v.boolean(),
      v.check((x): x is true => x === true, t('hoteliersPage.validation.privacy'))
    )
  })
)

interface HoteliersFormSchema {
  hotelName: string
  website?: string
  rooms: '1-9' | '10-19' | '20-59' | '60-99' | '100+'
  cityCountry: string
  contactName: string
  email: string
  phone: string
  comments: string
  privacyAccepted: boolean
}

const links = computed(() => [
  { label: t('hoteliersPage.breadcrumb.home'), to: `/${locale.value}` },
  { label: t('hoteliersPage.breadcrumb.current') }
])

usePublicSeo({
  title: () => t('meta.hoteliers.title'),
  description: () => t('meta.hoteliers.description'),
  ogTitle: () => t('meta.hoteliers.ogTitle'),
  ogDescription: () => t('meta.hoteliers.ogDescription'),
  path: '/hoteliers',
})

const FEATURE_ICONS = [
  'i-lucide-sliders-horizontal',
  'i-lucide-infinity',
  'i-lucide-scale',
  'i-lucide-trending-up',
  'i-lucide-users',
  'i-lucide-unlock'
] as const

const WIDGET_PILLAR_ICONS = ['i-lucide-puzzle', 'i-lucide-circle-dot', 'i-lucide-sliders-horizontal'] as const

const WIDGET_FEATURE_ICONS = ['i-lucide-palette', 'i-lucide-layout-template', 'i-lucide-smartphone'] as const

const STEP_ICONS = ['i-lucide-clipboard-pen-line', 'i-lucide-graduation-cap', 'i-lucide-badge-euro'] as const

const CASE_IMAGES = [
  '/img/hoteliers/hotel-barcelona.webp',
  '/img/hoteliers/hotel-madrid.webp',
  '/img/hoteliers/hotel-bogota.webp'
] as const

const DEMAND_IMAGES = [
  '/img/hoteliers/trabajo.webp',
  '/img/hoteliers/viajes.webp',
  '/img/hoteliers/microvacaciones.webp'
] as const

/** Misma longitud y orden que `hoteliersPage.steps.items` en los JSON de idioma. */
const STEP_LINK_TO_FORM = [true, false, false] as const

const ROOM_RANGE_VALUES = ['1-9', '10-19', '20-59', '60-99', '100+'] as const

function testimonialLogoIcon(kind: string): string | undefined {
  if (kind === 'hilton') return 'i-simple-icons-hiltonhotelsandresorts'
  if (kind === 'marriott') return 'i-simple-icons-marriott'
  if(kind === 'anonymous') return 'i-lucide-building-2'
  return undefined
}

const statsCards = computed(() =>
  Array.from({ length: 6 }, (_, i) => ({
    title: t(`hoteliersPage.stats.${i}.title`),
    description: t(`hoteliersPage.stats.${i}.description`)
  }))
)

const features = computed(() =>
  Array.from({ length: 6 }, (_, i) => ({
    icon: FEATURE_ICONS[i] ?? 'i-lucide-circle-dot',
    title: t(`hoteliersPage.features.${i}.title`),
    description: t(`hoteliersPage.features.${i}.description`)
  }))
)

const widgetWebPillars = computed(() =>
  Array.from({ length: 3 }, (_, i) => ({
    icon: WIDGET_PILLAR_ICONS[i] ?? 'i-lucide-puzzle',
    title: t(`hoteliersPage.widget.pillars.${i}.title`),
    description: t(`hoteliersPage.widget.pillars.${i}.description`)
  }))
)

const widgetWebFeatures = computed(() =>
  Array.from({ length: 3 }, (_, i) => ({
    icon: WIDGET_FEATURE_ICONS[i] ?? 'i-lucide-palette',
    title: t(`hoteliersPage.widget.widgetFeatures.${i}.title`),
    description: t(`hoteliersPage.widget.widgetFeatures.${i}.description`)
  }))
)

const testimonials = computed(() => {
  const resolveMessage = tm as (key: string) => unknown
  const items = resolveMessage('hoteliersPage.testimonials')
  if (!Array.isArray(items)) return []
  return (items as HoteliersTestimonialEntry[]).map((entry) => {
    const kind = String(entry.kind ?? '')
    const logoIcon = testimonialLogoIcon(kind)
    const user = {
      name: String(entry.name ?? ''),
      description: String(entry.role ?? ''),
      avatar: {}
    }
    const quote = String(entry.quote ?? '')
    if (logoIcon) {
      return { quote, logoIcon, user }
    }
    return { quote, user }
  })
})

const demandProfilePosts = computed((): DemandProfilePost[] =>
  Array.from({ length: 3 }, (_, i) => ({
    title: t(`hoteliersPage.demand.items.${i}.title`),
    paragraph: t(`hoteliersPage.demand.items.${i}.paragraph`),
    image: DEMAND_IMAGES[i] ?? DEMAND_IMAGES[0]!
  }))
)

const hoteliersBlogPosts = computed((): HoteliersBlogPost[] =>
  Array.from({ length: 3 }, (_, i) => ({
    badge: t(`hoteliersPage.examples.cases.${i}.badge`),
    title: t(`hoteliersPage.examples.cases.${i}.title`),
    description: t(`hoteliersPage.examples.cases.${i}.description`),
    revenue: t(`hoteliersPage.examples.cases.${i}.revenue`),
    bookings: t(`hoteliersPage.examples.cases.${i}.bookings`),
    image: CASE_IMAGES[i] ?? CASE_IMAGES[0]!
  }))
)

const partnerSteps = computed(() =>
  Array.from({ length: 3 }, (_, i) => ({
    icon: STEP_ICONS[i] ?? 'i-lucide-circle-dot',
    title: t(`hoteliersPage.steps.items.${i}.title`),
    description: t(`hoteliersPage.steps.items.${i}.description`),
    linkToForm: STEP_LINK_TO_FORM[i] ?? false
  }))
)

const formSectionHref = computed(() => `${localePath('/hoteliers')}#registro-hotelero`)

const faqItems = computed(() =>
  Array.from({ length: 8 }, (_, i) => ({
    label: t(`hoteliersPage.faq.items.${i}.label`),
    content: t(`hoteliersPage.faq.items.${i}.content`)
  }))
)

const roomItems = computed(() =>
  Array.from({ length: 5 }, (_, i) => ({
    label: t(`hoteliersPage.roomRanges.${i}.label`),
    value: ROOM_RANGE_VALUES[i] ?? ROOM_RANGE_VALUES[0]!
  }))
)

function cityCountryFromSelection(sel: CityCountrySelection): string {
  const city = sel.city?.trim() ?? ''
  const country = sel.country?.trim() ?? ''
  if (!city) return ''
  return country ? `${city}, ${country}` : city
}

const selectedLocation = ref<CityCountrySelection>({ city: '', country: '' })

const state = reactive<HoteliersFormSchema>({
  hotelName: '',
  website: '',
  rooms: '' as HoteliersFormSchema['rooms'],
  cityCountry: '',
  contactName: '',
  email: '',
  phone: '',
  comments: '',
  privacyAccepted: false
})

watch(() => [selectedLocation.value.city, selectedLocation.value.country] as const, () => {
  state.cityCountry = cityCountryFromSelection(selectedLocation.value)
  void syncFormValidation(form.value)
})

const form = ref<Form<HoteliersFormSchema>>()
const pending = ref(false)
const submitted = ref(false)
const serverError = ref('')
const toast = useToast()

function fetchErrorStatus(err: unknown): number | null {
  if (!err || typeof err !== 'object') return null
  const e = err as {
    statusCode?: unknown
    status?: unknown
    response?: { status?: unknown }
  }
  if (typeof e.statusCode === 'number') return e.statusCode
  if (typeof e.status === 'number') return e.status
  if (typeof e.response?.status === 'number') return e.response.status
  return null
}

async function onSubmit(event: FormSubmitEvent<HoteliersFormSchema>) {
  form.value?.clear()
  serverError.value = ''
  pending.value = true
  try {
    const res = await $fetch<{ ok: boolean }>('/api-web/hoteliers-lead', {
      method: 'POST',
      body: {
        hotelName: event.data.hotelName,
        website: event.data.website ?? '',
        rooms: event.data.rooms,
        cityCountry: event.data.cityCountry,
        contactName: event.data.contactName,
        email: event.data.email,
        phone: event.data.phone,
        comments: event.data.comments.trim(),
        locale: locale.value
      }
    })
    if (res?.ok) {
      trackHoteliersLeadFormSubmitted({
        locale: locale.value,
        rooms: event.data.rooms,
        has_website: Boolean(event.data.website?.trim()),
        has_comments: Boolean(event.data.comments.trim()),
        is_mobile: deviceStore.isMobile
      })
      submitted.value = true
      await nextTick()
      document.getElementById('registro-hotelero')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  } catch (err: unknown) {
    if (fetchErrorStatus(err) === 429) {
      serverError.value = t('hoteliersPage.errors.rateLimit')
    } else {
      const e = err as { data?: { statusMessage?: string }; message?: string }
      serverError.value =
        e?.data?.statusMessage || e?.message || t('hoteliersPage.errors.server')
    }
  } finally {
    pending.value = false
  }
}

async function onFormValidationError(event: {
  errors?: Array<{ name?: string; message?: string }>
}) {
  const errs = event.errors ?? []
  const first = errs.find((e) => e?.message)
  if (!first?.message) return
  toast.add({
    title: t('hoteliersPage.toast.validationTitle'),
    description: first.message,
    color: 'error'
  })
}
</script>

<template>
  <div class="max-w-screen-2xl container mx-auto px-5 relative flex flex-col gap-5">
    <UBreadcrumb class="py-4 sm:py-5" :items="links" />

    <UPageHero
      as="section"
      aria-labelledby="landing-hero-title"
      orientation="horizontal"
      :title="$t('hoteliersPage.hero.title')"
      :description="$t('hoteliersPage.hero.description')"
      :links="[
        {
          label: $t('hoteliersPage.hero.cta'),
          icon: 'i-lucide-building-2',
          size: 'lg',
          to: `${localePath('/hoteliers')}#registro-hotelero`,
          class: 'bg-byhours-gradient'
        }
      ]"
      :ui="{
        container: 'py-16 lg:py-24'
      }"
    >
      <div class="h-full w-full overflow-hidden rounded-3xl bg-neutral-200">
        <NuxtImg
          preload
          loading="eager"
          decoding="async"
          format="webp"
          quality="70"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 1200px"
          src="/img/hoteliers/cabecera.webp"
          :alt="$t('hoteliersPage.hero.imageAlt')"
          class="h-full w-full bg-neutral-200 object-cover"
        />
      </div>
    </UPageHero>

    <UPageSection
      :title="$t('hoteliersPage.whyTitle')"
      :ui="{
        title: 'text-2xl sm:text-3xl font-bold text-white',
        container: 'shadow-xl rounded-3xl bg-[radial-gradient(circle_1500px_at_10%_0px,#1F2937,#030712)]'
      }"
    >
      <template #features>
        <UPageFeature
          v-for="feature in features"
          :key="feature.title"
          as="li"
          :icon="feature.icon"
          :title="feature.title"
          :description="feature.description"
          :ui="{
            title: 'text-white',
            description: 'text-neutral-400'
          }"
        />
      </template>
    </UPageSection>

    <UPageSection
      :title="$t('hoteliersPage.whoWeAre.title')"
      :description="$t('hoteliersPage.whoWeAre.description')"
    >
      <template #body>
        <UPageGrid class="w-full">
          <UPageCard
            v-for="(item, index) in statsCards"
            :key="index"
            v-bind="item"
            variant="outline"
            spotlight
            spotlight-color="primary"
            :ui="{
              title: 'text-xl sm:text-3xl font-bold text-primary tabular-nums',
            }"
          />
        </UPageGrid>
        <div class="flex flex-col items-center gap-3 pt-8 sm:pt-10">
          <p class="text-center text-sm text-muted max-w-md">
            {{ $t('hoteliersPage.whoWeAre.ctaQuestion') }}
          </p>
          <UButton
            :to="formSectionHref"
            size="lg"
            icon="i-lucide-arrow-down-circle"
            class="bg-byhours-gradient motion-safe:transition-shadow motion-safe:hover:shadow-md"
          >
            {{ $t('hoteliersPage.whoWeAre.ctaButton') }}
          </UButton>
        </div>
      </template>
    </UPageSection>

    <UPageSection
      :title="$t('hoteliersPage.video.title')"
      :description="$t('hoteliersPage.video.description')"
    >
      <div class="aspect-video w-full min-w-0 overflow-hidden rounded-3xl ring ring-default">
        <iframe
          class="h-full w-full"
          src="https://www.youtube.com/embed/74hMPca-KGg"
          :title="$t('hoteliersPage.video.iframeTitle')"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
        />
      </div>
    </UPageSection>

    <UPageSection
      id="widget-web"
      :title="$t('hoteliersPage.widget.title')"
      :description="$t('hoteliersPage.widget.description')"
    >
      <template #features>
        <UPageFeature
          v-for="pillar in widgetWebPillars"
          :key="pillar.title"
          as="li"
          :icon="pillar.icon"
          :title="pillar.title"
          :description="pillar.description"
        />
      </template>
      <template #body>
        <figure class="mx-auto w-full max-w-5xl mb-16">
          <NuxtImg
            src="/img/hoteliers/widget.png"
            :alt="$t('hoteliersPage.widget.imageAlt')"
            class="w-full rounded-2xl ring ring-default shadow-lg"
            sizes="(max-width: 640px) 100vw, 896px"
            format="webp"
            loading="lazy"
            decoding="async"
          />
          <figcaption class="mt-3 text-center text-xs text-muted">
            {{ $t('hoteliersPage.widget.figcaption') }}
          </figcaption>
        </figure>
        <div class="flex w-full flex-col gap-10 sm:gap-12">
          <UPageColumns>
            <UPageFeature
              v-for="feature in widgetWebFeatures"
              :key="feature.title"
              :icon="feature.icon"
              :title="feature.title"
              :description="feature.description"
            />
          </UPageColumns>
          <div class="flex flex-col items-center gap-3">
            <UButton
              :to="formSectionHref"
              size="lg"
              icon="i-lucide-info"
              class="bg-byhours-gradient motion-safe:transition-shadow motion-safe:hover:shadow-md"
            >
              {{ $t('hoteliersPage.widget.cta') }}
            </UButton>
          </div>
        </div>
      </template>
    </UPageSection>

    <UPageSection
      :title="$t('hoteliersPage.examples.title')"
      :description="$t('hoteliersPage.examples.description')"
    >
      <template #body>
        <div class="flex flex-col gap-6">
          <UBlogPosts :posts="hoteliersBlogPosts">
            <template #description="{ post }">
              <p class="text-muted text-sm leading-relaxed">{{ asHoteliersBlogPost(post).description }}</p>
            </template>
            <template #footer="{ post }">
              <div class="space-y-1 p-4 pt-0 sm:p-6 sm:pt-0">
                <p class="text-2xl font-bold tabular-nums text-byhours-gradient sm:text-3xl w-fit">
                  {{ asHoteliersBlogPost(post).revenue }}
                </p>
                <p class="text-xs text-muted">{{ $t('hoteliersPage.examples.benefitLabel') }}</p>
                <p class="pt-2 text-lg font-semibold tabular-nums text-highlighted">
                  {{ asHoteliersBlogPost(post).bookings }}
                </p>
                <p class="text-xs text-muted">{{ $t('hoteliersPage.examples.bookingsLabel') }}</p>
              </div>
            </template>
          </UBlogPosts>
          <div class="flex justify-center">
            <UButton
              :to="formSectionHref"
              size="lg"
              icon="i-lucide-building-2"
              class="bg-byhours-gradient motion-safe:transition-shadow motion-safe:hover:shadow-md"
            >
              {{ $t('hoteliersPage.examples.cta') }}
            </UButton>
          </div>
        </div>
      </template>
    </UPageSection>

    <UPageSection :title="$t('hoteliersPage.testimonialsTitle')">
      <template #body>
        <UPageColumns>
          <UPageCard
            v-for="(testimonial, index) in testimonials"
            :key="index"
            variant="subtle"
            :description="testimonial.quote"
            :icon="'logoIcon' in testimonial ? testimonial.logoIcon : undefined"
            :ui="{
              leadingIcon: 'size-10 text-highlighted shrink-0',
              description: 'whitespace-pre-line'
            }"
          >
            <template v-if="!('logoIcon' in testimonial)" #leading>
              <svg
                class="h-10 w-auto max-w-32 shrink-0 text-highlighted fill-current mb-2"
                viewBox="0 0 517 218"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M371.461 151.583V112.938H376.391V129.647H389.922V112.938H394.851V151.583H389.922V133.915H376.391V151.583H371.461Z"
                />
                <path
                  d="M402.329 137.468V124.796C402.329 117.087 406.299 112.157 414.471 112.157C422.643 112.157 426.613 117.087 426.613 124.796V137.468C426.613 148.155 422.643 152.39 414.471 152.39C406.299 152.39 402.329 148.188 402.329 137.468ZM421.617 137.7V124.631C421.617 116.558 416.059 116.293 414.504 116.293C412.949 116.293 407.391 116.558 407.391 124.631V137.7C407.391 146.17 409.839 148.287 414.504 148.287C419.136 148.287 421.617 146.137 421.617 137.7Z"
                />
                <path d="M455.53 112.938V117.206H445.374V151.55H440.444V117.206H430.287V112.938H455.53Z" />
                <path
                  d="M459.301 151.583V112.938H479.217V117.206H464.23V129.613H478.159V133.882H464.23V147.248H479.548V151.516H459.301V151.583Z"
                />
                <path d="M484.708 151.583V112.938H489.637V147.281H503.831V151.55H484.708V151.583Z" />
                <path
                  d="M389.658 187.527C389.658 182.994 387.772 180.513 382.81 180.513C381.255 180.513 375.697 180.777 375.697 188.85V201.919C375.697 210.389 378.145 212.507 382.81 212.507C386.35 212.507 388.699 211.547 389.724 210.786V199.239H382.611V194.971H394.654V213.764C391.478 215.485 387.806 216.643 382.81 216.643C374.638 216.643 370.668 212.408 370.668 201.721V189.049C370.668 181.34 374.638 176.41 382.81 176.41C391.147 176.41 395.117 179.454 394.786 187.527H389.658Z"
                />
                <path
                  d="M407.326 198.045V215.812H402.396V177.167H415.729C422.578 177.167 424.993 181.965 424.993 186.796C424.993 191.361 422.479 194.835 417.946 195.629V195.729C422.379 196.424 424 197.912 424.265 205.688C424.331 207.342 424.861 213.397 425.588 215.812H420.46C419.071 213.132 419.402 208.103 418.972 202.975C418.608 198.276 414.803 198.045 413.149 198.045H407.326ZM407.326 193.777H414.704C418.244 193.777 419.931 190.733 419.931 187.358C419.931 184.579 418.542 181.469 414.737 181.469H407.293V193.777H407.326Z"
                />
                <path
                  d="M430.748 201.691V189.019C430.748 181.31 434.718 176.38 442.89 176.38C451.062 176.38 455.032 181.31 455.032 189.019V201.691C455.032 212.378 451.062 216.613 442.89 216.613C434.718 216.646 430.748 212.411 430.748 201.691ZM450.036 201.923V188.853C450.036 180.78 444.478 180.516 442.923 180.516C441.368 180.516 435.81 180.78 435.81 188.853V201.923C435.81 210.393 438.258 212.51 442.923 212.51C447.555 212.51 450.036 210.36 450.036 201.923Z"
                />
                <path
                  d="M462.111 177.213H467.041V201.929C467.041 210.399 469.621 212.516 474.253 212.516C478.918 212.516 481.465 210.366 481.465 201.929V177.213H486.395V201.73C486.395 212.417 482.425 216.652 474.253 216.652C466.048 216.652 462.111 212.417 462.111 201.73V177.213Z"
                />
                <path
                  d="M494.27 215.812V177.167H505.717C510.911 177.167 516.469 179.086 516.469 188.185C516.469 196.953 509.62 198.773 505.65 198.773H499.166V215.779H494.27V215.812ZM499.199 194.538H504.592C506.61 194.538 511.44 194.008 511.44 187.954C511.44 182.064 507.04 181.469 505.981 181.469H499.199V194.538Z"
                />
                <path
                  d="M162.139 140.379C162.139 128.27 157.144 121.719 143.91 121.719C139.774 121.719 124.986 122.414 124.986 143.92V178.66C124.986 201.159 131.537 206.849 143.91 206.849C153.306 206.849 159.559 204.302 162.272 202.284V171.547H143.348V160.165H175.373V210.125C166.97 214.691 157.144 217.801 143.91 217.801C122.141 217.801 111.587 206.552 111.587 178.064V144.317C111.587 123.803 122.108 110.701 143.91 110.701C166.109 110.701 176.663 118.807 175.803 140.313H162.139V140.379Z"
                />
                <path
                  d="M235.721 141.239C235.853 135.416 235.423 129.56 232.743 126.284C230.03 123.009 223.91 121.884 221.197 121.884C210.378 121.884 206.111 128.435 205.68 129.858C205.25 130.982 202.703 133.563 202.703 151.231V178.693C202.703 203.904 210.974 206.882 221.064 206.882C225.035 206.882 237.143 205.459 237.309 185.376H250.84C251.403 217.834 228.475 217.834 221.792 217.834C208.989 217.834 189.337 216.973 189.337 177.105V148.055C189.337 119.005 202.14 110.767 222.355 110.767C242.701 110.767 250.542 121.288 249.252 141.239H235.721Z"
                />
                <path
                  d="M262.814 215.678V112.879H275.916V157.281H311.944V112.879H325.045V215.678H311.944V168.696H275.916V215.678H262.814Z"
                />
                <path d="M349.762 112.818H346.453V215.716H349.762V112.818Z" />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M1.65664 101.853C0.570035 96.4248 0 90.8105 0 85.0631C0 83.0819 0.0677393 81.1165 0.201017 79.1691H56.5932C69.1213 79.1691 79.2774 69.0133 79.2774 56.4856V0.193829C81.1901 0.0653036 83.1203 0 85.0656 0C90.8506 0 96.5008 0.577451 101.962 1.67786V33.8243C101.962 46.352 112.118 56.5077 124.646 56.5077H165.22C167.771 63.6679 169.39 71.2703 169.93 79.1691H101.962C89.4334 79.1691 79.2774 89.3248 79.2774 101.853V169.932C71.3712 169.401 63.761 167.79 56.5932 165.244V124.536C56.5932 112.008 46.4371 101.853 33.909 101.853H1.65664Z"
                />
              </svg>
            </template>
            <template #footer>
              <UUser v-bind="testimonial.user" />
            </template>
          </UPageCard>
        </UPageColumns>
      </template>
    </UPageSection>

    <UPageSection
      :title="$t('hoteliersPage.demand.title')"
      :description="$t('hoteliersPage.demand.description')"
    >
      <template #body>
        <UBlogPosts>
          <UBlogPost
            v-for="post in demandProfilePosts"
            :key="post.title"
            :title="post.title"
            :description="post.paragraph"
            :image="post.image"
            variant="naked"
            :ui="{
              header: 'rounded-2xl',
            }"
          />
        </UBlogPosts>
      </template>
    </UPageSection>

    <UPageSection
      :title="$t('hoteliersPage.steps.title')"
      :description="$t('hoteliersPage.steps.description')"
    >
      <template #body>
        <UPageGrid>
          <template v-for="(step, index) in partnerSteps" :key="index">
            <NuxtLink
              v-if="step.linkToForm"
              :to="formSectionHref"
              class="group block h-full rounded-3xl outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-default"
            >
              <UPageCard
                variant="outline"
                spotlight
                spotlight-color="primary"
                class="h-full transition group-hover:ring-1 group-hover:ring-primary/35"
                :ui="{ root: 'h-full' }"
              >
                <template #description>
                  <UPageFeature
                    orientation="vertical"
                    :icon="step.icon"
                    :title="step.title"
                    :description="step.description"
                  />
                </template>
              </UPageCard>
            </NuxtLink>
            <UPageCard
              v-else
              variant="outline"
              spotlight
              spotlight-color="primary"
            >
              <template #description>
                <UPageFeature
                  orientation="vertical"
                  :icon="step.icon"
                  :title="step.title"
                  :description="step.description"
                />
              </template>
            </UPageCard>
          </template>
        </UPageGrid>
      </template>
    </UPageSection>

    <UPageSection
      id="registro-hotelero"
      :title="$t('hoteliersPage.form.title')"
      :description="$t('hoteliersPage.form.description')"
    >
      <template #body>
        <div class="max-w-xl mx-auto w-full">
          <template v-if="submitted">
            <div class="flex flex-col gap-4">
              <UAlert
                icon="i-lucide-check-circle-2"
                color="success"
                variant="subtle"
                role="status"
                aria-live="polite"
                :title="$t('hoteliersPage.form.successTitle')"
                :description="$t('hoteliersPage.form.successDescription')"
              />
            </div>
          </template>

          <UForm
            v-else
            ref="form"
            :schema="schema"
            class="flex flex-col gap-6"
            :state="state"
            @submit="onSubmit"
            @error="onFormValidationError"
          >
            <div class="rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-xs">
              <div class="flex flex-col gap-8 sm:gap-10">
                <section aria-labelledby="hoteliers-sec-hotel" class="space-y-4 min-w-0">
                  <div class="flex items-center gap-2 min-w-0">
                    <UIcon name="i-lucide-building-2" class="size-5 text-primary shrink-0" aria-hidden="true" />
                    <h2 id="hoteliers-sec-hotel" class="text-base">{{ $t('hoteliersPage.form.hotelSection') }}</h2>
                  </div>
                  <div class="space-y-4">
                    <UFormField name="hotelName" :label="$t('hoteliersPage.form.hotelName')" required>
                      <UInput
                        v-model="state.hotelName"
                        class="w-full min-w-0 rounded-md"
                        size="lg"
                        autocomplete="organization"
                        :disabled="pending"
                      />
                    </UFormField>
                    <UFormField name="website" :label="$t('hoteliersPage.form.website')">
                      <UInput
                        v-model="state.website"
                        class="w-full min-w-0 rounded-md"
                        size="lg"
                        type="url"
                        inputmode="url"
                        :placeholder="$t('hoteliersPage.form.websitePlaceholder')"
                        :disabled="pending"
                      />
                    </UFormField>
                    <UFormField name="rooms" :label="$t('hoteliersPage.form.rooms')" required>
                      <USelect
                        v-model="state.rooms"
                        :items="roomItems"
                        value-key="value"
                        label-key="label"
                        size="lg"
                        :placeholder="$t('hoteliersPage.form.roomsPlaceholder')"
                        class="w-full min-w-0 rounded-md"
                        :disabled="pending"
                      />
                    </UFormField>
                    <UFormField name="cityCountry" :label="$t('hoteliersPage.form.cityCountry')" required>
                      <PlacesAutocomplete
                        v-model="selectedLocation"
                        class="rounded-md"
                        :placeholder="$t('hoteliersPage.form.cityPlaceholder')"
                        :disabled="pending"
                      />
                    </UFormField>
                  </div>
                </section>

                <section aria-labelledby="hoteliers-sec-contact" class="space-y-4 min-w-0">
                  <div class="flex items-center gap-2 min-w-0">
                    <UIcon name="i-lucide-user-round" class="size-5 text-primary shrink-0" aria-hidden="true" />
                    <h2 id="hoteliers-sec-contact" class="text-base">{{ $t('hoteliersPage.form.contactSection') }}</h2>
                  </div>
                  <div class="space-y-4">
                    <UFormField name="contactName" :label="$t('hoteliersPage.form.contactName')" required>
                      <UInput
                        v-model="state.contactName"
                        class="w-full min-w-0 rounded-md"
                        size="lg"
                        autocomplete="name"
                        :disabled="pending"
                      />
                    </UFormField>
                    <UFormField name="email" :label="$t('hoteliersPage.form.email')" required>
                      <UInput
                        v-model="state.email"
                        class="w-full min-w-0 rounded-md"
                        size="lg"
                        type="email"
                        autocomplete="email"
                        :spellcheck="false"
                        :disabled="pending"
                      />
                    </UFormField>
                    <UFormField name="phone" :label="$t('hoteliersPage.form.phone')" required>
                      <PhoneFieldGroup
                        v-model="state.phone"
                        class="w-full min-w-0"
                        :disabled="pending"
                      />
                    </UFormField>
                    <UFormField name="comments" :label="$t('hoteliersPage.form.comments')">
                      <UTextarea
                        v-model="state.comments"
                        class="w-full min-w-0 rounded-md"
                        size="lg"
                        :rows="4"
                        :placeholder="$t('hoteliersPage.form.commentsPlaceholder')"
                        :disabled="pending"
                      />
                    </UFormField>
                  </div>
                </section>

                <section aria-labelledby="hoteliers-sec-send" class="space-y-4 min-w-0">
                  <UFormField name="privacyAccepted">
                    <UCheckbox v-model="state.privacyAccepted" :disabled="pending">
                      <template #label>
                        <span class="text-sm text-muted leading-relaxed">
                          {{ $t('hoteliersPage.form.privacyBefore') }}
                          <NuxtLink
                            :to="localePath('/privacy.html')"
                            class="text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
                            target="_blank"
                            rel="noopener noreferrer"
                            @click.stop
                          >
                            {{ $t('hoteliersPage.form.privacyLink') }}
                          </NuxtLink>
                          {{ $t('hoteliersPage.form.privacyAfter') }}
                        </span>
                      </template>
                    </UCheckbox>
                  </UFormField>

                  <UAlert
                    v-if="serverError"
                    icon="i-lucide-alert-circle"
                    :title="serverError"
                    color="error"
                    variant="soft"
                    role="alert"
                  />

                  <UButton
                    type="submit"
                    class="w-full justify-center bg-byhours-gradient motion-safe:transition-opacity"
                    variant="solid"
                    size="xl"
                    :loading="pending"
                    :disabled="pending"
                  >
                    {{ $t('hoteliersPage.form.submit') }}
                  </UButton>
                </section>
              </div>
            </div>
          </UForm>
        </div>
      </template>
    </UPageSection>

    <UPageSection
      id="faq-title"
      orientation="horizontal"
      :title="$t('hoteliersPage.faq.title')"
      :description="$t('hoteliersPage.faq.description')"
    >
      <UAccordion
        :ui="{
          root: 'max-w-2xl mx-auto'
        }"
        :items="faqItems"
      />
    </UPageSection>
  </div>
</template>
